import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { getActiveModal, subscribeToModalLayer } from './modalLayer';

const SLOP = 8;
const editable =
  'textarea, select, input[type="range"], input[type="file"], input[type="date"], input[type="time"], [contenteditable]:not([contenteditable="false"]), [data-sheet-swipe="ignore"]';

function translateY(dialog: HTMLDialogElement): number {
  const transform = getComputedStyle(dialog).transform;
  if (!transform || transform === 'none') return 0;
  const values = transform.match(/matrix(3d)?\(([^)]+)\)/);
  if (!values) return 0;
  return Number(values[2].split(',')[values[1] ? 13 : 5]) || 0;
}

function isScrolled(target: Element, dialog: HTMLDialogElement): boolean {
  for (let node: Element | null = target; node; node = node.parentElement) {
    if (node.scrollTop > 0) return true;
    if (node === dialog) break;
  }
  return false;
}

export function useSheetSwipe(
  ref: RefObject<HTMLDialogElement | null>,
  enabled: boolean,
  open: boolean,
  onDismiss: () => void
) {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const wasOpen = useRef(false);
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !wasOpen.current) {
      delete dialog.dataset.swipe;
      dialog.style.removeProperty('--sheet-offset');
      dialog.style.removeProperty('--sheet-close-from');
    } else if (!open && wasOpen.current) {
      dialog.style.setProperty('--sheet-close-from', `${translateY(dialog)}px`);
      delete dialog.dataset.swipe;
    }
    wasOpen.current = open;
  }, [open, ref]);

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog || !enabled || !open) return;
    let gesture: {
      x: number;
      y: number;
      base: number;
      dragging: boolean;
      lastY: number;
      lastTime: number;
      velocity: number;
    } | null = null;
    let dismissFrame = 0;
    const settle = () => {
      if (dialog.dataset.swipe === 'dragging') {
        dialog.dataset.swipe = 'settling';
        dialog.style.setProperty('--sheet-offset', '0px');
      }
      gesture = null;
    };
    const start = (event: TouchEvent) => {
      cancelAnimationFrame(dismissFrame);
      settle();
      if (event.touches.length !== 1) return;
      const target = event.target;
      if (
        !(target instanceof Element) ||
        getActiveModal() !== dialog ||
        target.closest(editable) ||
        (target instanceof HTMLInputElement &&
          target === document.activeElement &&
          !['checkbox', 'radio', 'button', 'submit'].includes(target.type)) ||
        window.getSelection()?.isCollapsed === false ||
        isScrolled(target, dialog)
      )
        return;
      const touch = event.touches[0];
      if (target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (
          touch.clientX < rect.left ||
          touch.clientX > rect.right ||
          touch.clientY < rect.top ||
          touch.clientY > rect.bottom
        )
          return;
      }
      gesture = {
        x: touch.clientX,
        y: touch.clientY,
        base: 0,
        dragging: false,
        lastY: touch.clientY,
        lastTime: event.timeStamp,
        velocity: 0,
      };
    };
    const move = (event: TouchEvent) => {
      if (!gesture) return;
      if (event.touches.length !== 1 || window.getSelection()?.isCollapsed === false) {
        settle();
        return;
      }
      const touch = event.touches[0];
      const dy = touch.clientY - gesture.y;
      const dx = touch.clientX - gesture.x;
      if (!gesture.dragging) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP) return;
        if (
          dy <= 0 ||
          dy < Math.abs(dx) * 1.2 ||
          !event.cancelable ||
          (event.target instanceof Element && isScrolled(event.target, dialog))
        ) {
          gesture = null;
          return;
        }
        gesture.base = translateY(dialog);
        gesture.dragging = true;
        dialog.dataset.swipe = 'dragging';
      }
      if (!event.cancelable) {
        settle();
        return;
      }
      event.preventDefault();
      const elapsed = event.timeStamp - gesture.lastTime;
      if (elapsed > 0) gesture.velocity = (touch.clientY - gesture.lastY) / elapsed;
      gesture.lastY = touch.clientY;
      gesture.lastTime = event.timeStamp;
      dialog.style.setProperty('--sheet-offset', `${Math.max(0, gesture.base + dy)}px`);
    };
    const end = (event: TouchEvent) => {
      if (!gesture) return;
      if (gesture.dragging && event.cancelable) event.preventDefault();
      if (event.touches.length > 0) {
        settle();
        return;
      }
      const drag = gesture;
      const distance = Math.max(0, drag.lastY - drag.y);
      const fast = event.timeStamp - drag.lastTime < 100 && drag.velocity > 0.65 && distance >= 32;
      if (
        drag.dragging &&
        (distance >= Math.min(160, Math.max(64, dialog.getBoundingClientRect().height * 0.3)) ||
          fast)
      ) {
        gesture = null;
        // A controlled owner may refuse to close; let its update commit before returning the sheet.
        dismissFrame = requestAnimationFrame(() => {
          if (dialog.dataset.state === 'open') settle();
        });
        dismissRef.current();
      } else settle();
    };
    const stopLayer = subscribeToModalLayer(() => {
      if (getActiveModal() !== dialog) settle();
    });
    dialog.addEventListener('touchstart', start, { passive: true });
    dialog.addEventListener('touchmove', move, { passive: false });
    dialog.addEventListener('touchend', end, { passive: false });
    dialog.addEventListener('touchcancel', settle);
    return () => {
      cancelAnimationFrame(dismissFrame);
      stopLayer();
      dialog.removeEventListener('touchstart', start);
      dialog.removeEventListener('touchmove', move);
      dialog.removeEventListener('touchend', end);
      dialog.removeEventListener('touchcancel', settle);
      // Preserve the captured position during closing; abort an operation that became blocked.
      if (dialog.dataset.state === 'open') settle();
    };
  }, [enabled, open, ref]);
}
